import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, KeyRound, Sparkles, UserCircle } from 'lucide-react'
import { exchangeApiKeyForSession, fetchApi } from '../lib/api'
import { useAuthStore } from '../store/auth'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/'
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated)

  const [key, setKey] = useState('')
  const [show, setShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const [oauthLoading, setOauthLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const setApiKey = useAuthStore((s) => s.setApiKey)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmed = key.trim()
    if (!trimmed.startsWith('orz_')) {
      setError('La API key debe comenzar con orz_')
      return
    }

    setLoading(true)
    try {
      await exchangeApiKeyForSession(trimmed)
      setApiKey(trimmed)
      setAuthenticated(true)
      navigate(from, { replace: true })
      window.location.reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error de conexión')
    } finally {
      setLoading(false)
    }
  }

  async function handleOrzattyLogin() {
    setOauthLoading(true)
    setError(null)
    try {
      const redirectUri = `${window.location.origin}/auth/callback`
      const res = (await fetchApi('/v1/auth/orzatty/initiate', {
        method: 'POST',
        body: JSON.stringify({ redirect_uri: redirectUri }),
      })) as { authorize_url: string }
      window.location.assign(res.authorize_url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo iniciar el login con Orzatty')
      setOauthLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-panel p-8 shadow-2xl">
        <div className="mb-6 flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 shadow-lg shadow-brand-500/25">
            <Sparkles className="h-7 w-7 text-white" />
          </div>
        </div>
        <h1 className="mb-2 text-center text-2xl font-bold">Bienvenido a OrzForge</h1>
        <p className="mb-6 text-center text-sm text-gray-400">
          Inicia sesión con tu cuenta de Orzatty o usa una API key existente.
        </p>

        <div className="space-y-3">
          <button
            type="button"
            onClick={handleOrzattyLogin}
            disabled={oauthLoading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 shadow transition hover:bg-gray-100 disabled:opacity-60"
          >
            <UserCircle className="h-5 w-5" />
            {oauthLoading ? 'Redirigiendo...' : 'Continuar con Orzatty'}
          </button>

          <div className="relative flex items-center py-2">
            <div className="flex-grow border-t border-white/10" />
            <span className="mx-3 text-xs text-gray-500">o</span>
            <div className="flex-grow border-t border-white/10" />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label htmlFor="apiKey" className="mb-1.5 block text-sm font-medium text-gray-300">
              API Key
            </label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <input
                id="apiKey"
                type={show ? 'text' : 'password'}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="orz_..."
                className="w-full rounded-lg border border-white/10 bg-surface py-2.5 pl-10 pr-10 text-sm text-white placeholder-gray-600 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 hover:text-gray-300"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-600/20 transition hover:bg-brand-500 disabled:opacity-60"
          >
            {loading ? 'Verificando...' : 'Ingresar al dashboard'}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-gray-500">
          La API key no se guarda en el navegador; se intercambia una sola vez por una sesión segura.
        </p>
      </div>
    </div>
  )
}
